'use client';

import { useEffect, useRef } from 'react';
import { usePageVisibility } from './usePageVisibility';

interface UseHeartbeatOptions {
  interval: number; // milliseconds
  enabled?: boolean;
}

export const useHeartbeat = (callback: () => void, options: UseHeartbeatOptions) => {
  const { enabled = true, interval } = options;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisible = usePageVisibility();

  const startHeartbeat = () => {
    if (!enabled) return;

    intervalRef.current = setInterval(() => {
      callback();
    }, interval);
  };

  const stopHeartbeat = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isVisible && enabled) {
      startHeartbeat();
    } else {
      stopHeartbeat();
    }

    return () => {
      stopHeartbeat();
    };
  }, [isVisible, enabled, interval, callback]);

  return {
    isActive: isVisible && enabled && intervalRef.current !== null,
    start: startHeartbeat,
    stop: stopHeartbeat,
  };
};
